/**
 * Admin Question Bank — CSV bulk import page (Story 60).
 *
 * Two-step UX:
 *   1. Drag-drop or click-to-select a `.csv` file. The browser reads it
 *      locally and renders a preview (column headers + first 5 rows + total
 *      row count). Nothing is sent to the server in this step.
 *   2. Click "Run import" → POST the CSV to `/api/questions/import`. On
 *      response, render the row-by-row outcome table: inserted, updated,
 *      and per-row errors.
 *
 * The "preview" half is the user-facing dry-run — the server's
 * `importQuestionsFromCsv` signature has no dry-run flag (the Module
 * surface is locked by docs/modules.md § Module 2) and we don't want to
 * add a column-only "did anything change" round-trip on every preview.
 */

import { useEffect, useRef, useState, type DragEvent, type ReactElement } from 'react';
import { AdminLayout } from '../layout.tsx';
import { Button, Link } from '@/components/ui';
import {
  importQuestionsCsv,
  type CsvImportResult,
} from '../../../state/questions/api.ts';

interface Preview {
  filename: string;
  bytes: number;
  csvText: string;
  headers: string[];
  rowCount: number;
  firstRows: string[][];
}

function parsePreview(csvText: string, filename: string, bytes: number): Preview {
  // Lightweight preview parser — splits on raw newlines + commas. The
  // server's parser is the authoritative one; this is just for the
  // header + row-count display so users see what they're about to upload.
  const cleaned = csvText.replace(/\r\n?/g, '\n');
  const lines = cleaned.split('\n').filter((l) => l.length > 0);
  const header = lines[0] ?? '';
  const headers = header.split(',').map((h) => h.trim());
  const dataLines = lines.slice(1);
  const firstRows = dataLines.slice(0, 5).map((l) => l.split(','));
  return {
    filename,
    bytes,
    csvText,
    headers,
    rowCount: dataLines.length,
    firstRows,
  };
}

export function AdminQuestionImportPage(): ReactElement {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset error when a new preview is set, but keep the previous result
  // visible until the user confirms a new run.
  useEffect(() => {
    if (preview) setError(null);
  }, [preview]);

  const readFile = (file: File): void => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are accepted.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setPreview(parsePreview(text, file.name, file.size));
      setResult(null);
    };
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsText(file);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) readFile(file);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = (): void => setDragOver(false);

  const onPickFile = (): void => {
    inputRef.current?.click();
  };

  const onSubmit = (): void => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    void (async () => {
      const env = await importQuestionsCsv(preview.csvText);
      setSubmitting(false);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        return;
      }
      setResult(env.data);
    })();
  };

  const reset = (): void => {
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <AdminLayout title='Bulk Import' eyebrow='Admin · Questions'>
      <p>
        <Link to="/admin/questions">← Back to Question Bank</Link>
      </p>

      {preview === null ? (
        <div
          className={`qb-csv-drop ${dragOver ? 'qb-csv-drop--over' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          role="button"
          tabIndex={0}
          onClick={onPickFile}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onPickFile();
          }}
        >
          <p>Drop a .csv file here, or click to select.</p>
          <p className="qb-csv-drop-hint">
            Sandbox v0 column shape:{' '}
            <code>
              type,text,option_1..option_n,correct,explanation,category,subcategory,tags,benchmark_domains
            </code>
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) readFile(f);
            }}
          />
        </div>
      ) : (
        <div className="qb-csv-preview">
          <h2>Preview</h2>
          <p>
            <strong>{preview.filename}</strong> — {preview.rowCount} row
            {preview.rowCount === 1 ? '' : 's'} ({preview.bytes} bytes)
          </p>
          <table className="qb-csv-preview-table">
            <thead>
              <tr>
                {preview.headers.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.firstRows.map((row, i) => (
                <tr key={String(i)}>
                  {row.map((cell, j) => (
                    <td key={`${String(i)}-${String(j)}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {preview.rowCount > 5 ? (
            <p className="qb-csv-preview-more">
              … and {preview.rowCount - 5} more row
              {preview.rowCount - 5 === 1 ? '' : 's'}.
            </p>
          ) : null}
          <div className="qb-csv-actions">
            <Button variant="primary" type="button" onClick={onSubmit} disabled={submitting}>
              {submitting ? 'Importing…' : 'Run import'}
            </Button>
            <Button variant="secondary" type="button" onClick={reset} disabled={submitting}>
              Choose another file
            </Button>
          </div>
        </div>
      )}

      {error ? <p className="qb-admin-error">{error}</p> : null}

      {result ? (
        <div className="qb-csv-result">
          <h2>Import outcome</h2>
          <p>
            Inserted <strong>{result.inserted}</strong>, updated{' '}
            <strong>{result.updated}</strong>, errors{' '}
            <strong>{result.errors.length}</strong>.
          </p>
          {result.errors.length > 0 ? (
            <table className="qb-csv-errors-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {result.errors.map((e) => (
                  <tr key={`${String(e.row)}-${e.message}`}>
                    <td>{e.row}</td>
                    <td>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </AdminLayout>
  );
}
